"use client";

import { PaginationControls } from "@/components/ui/pagination";
import { ListSkeleton } from "@/components/shared";
import { PublisherGamesList } from "./PublisherGamesList";
import { useListPagination } from "@/hooks";
import type { PublisherGame } from "../types";
import {
  DEFAULT_PUBLISHER_GAMES_SORT,
  type PublisherGamesSort,
} from "../utils/publisherGamesSort";
import { publisherGamesSkeletonConfig } from "../config";

interface PublisherGamesListWithPaginationProps {
  initialGames: PublisherGame[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  clanid: number;
  type?: string | null;
  comingSoon?: boolean;
  initialSort: PublisherGamesSort;
}

async function loadPublisherGames(
  clanid: number,
  page: number,
  pageSize: number,
  type?: string | null,
  comingSoon?: boolean,
  sort?: PublisherGamesSort | null,
): Promise<{ items: PublisherGame[]; total: number }> {
  const { getPublisherGamesClient } =
    await import("../api/getPublisherGamesClient");
  const offset = (page - 1) * pageSize;
  const { games, total } = await getPublisherGamesClient(
    clanid,
    pageSize,
    offset,
    type,
    comingSoon,
    sort ?? DEFAULT_PUBLISHER_GAMES_SORT,
  );
  return { items: games, total };
}

export function PublisherGamesListWithPagination({
  initialGames,
  initialPage,
  initialTotal,
  pageSize,
  clanid,
  type,
  comingSoon,
  initialSort,
}: PublisherGamesListWithPaginationProps) {
  const {
    items: games,
    currentPage,
    currentSort,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    handlePageChange,
    handleSortChange,
    handlePageSizeChange,
  } = useListPagination<PublisherGame, PublisherGamesSort>({
    initialItems: initialGames,
    initialPage,
    initialTotal,
    pageSize,
    sortable: true,
    initialSort,
    onPageChange: (page, pageSize, sort) =>
      loadPublisherGames(clanid, page, pageSize, type, comingSoon, sort),
  });

  return (
    <div className="space-y-8">
      {isPending ? (
        <ListSkeleton columns={publisherGamesSkeletonConfig} />
      ) : (
        <PublisherGamesList
          games={games}
          sortBy={currentSort ?? DEFAULT_PUBLISHER_GAMES_SORT}
          onSortChange={handleSortChange!}
        />
      )}

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={currentPageSize}
        onPageSizeChange={handlePageSizeChange!}
        disabled={isPending}
      />
    </div>
  );
}
